import type { SubdomainInfo } from '../types/tenant.types';

let authClient = 'admin-bff';

export interface BootstrapConfig {
	authClient?: string;
}

export function configureBootstrap(opts: BootstrapConfig) {
	if (opts.authClient) authClient = opts.authClient;
}

export function getAuthClient(): string {
	return authClient;
}

/**
 * Extract subdomain from current hostname
 *
 * Examples:
 *   - tenant1.example.com → "tenant1"
 *   - doorpin-dev.talejutech.com → "doorpin-dev"
 *   - doorpin-dev.localhost:5173 → "doorpin-dev"
 *   - localhost:3000 → null
 *   - example.com → null
 */
export function detectSubdomain(): SubdomainInfo {
	const hostname = window.location.hostname;
	const port = window.location.port ? parseInt(window.location.port) : null;

	// IP addresses have no subdomains
	const isIpAddress = /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
	if (hostname === 'localhost' || isIpAddress) {
		return {
			subdomain: null,
			domain: hostname,
			fullHostname: hostname,
			isLocalhost: true,
			port,
		};
	}

	// Split hostname into parts
	const parts = hostname.split('.');

	// *.localhost resolves to loopback in browsers — base domain is the single "localhost" label
	const isLocalhost = parts[parts.length - 1] === 'localhost';
	const baseDomainLabels = isLocalhost ? 1 : 2;

	if (parts.length <= baseDomainLabels) {
		return {
			subdomain: null,
			domain: hostname,
			fullHostname: hostname,
			isLocalhost,
			port,
		};
	}

	// Subdomain is everything before the base domain
	const subdomain = parts.slice(0, -baseDomainLabels).join('.');
	const domain = parts.slice(-baseDomainLabels).join('.');

	return {
		subdomain,
		domain,
		fullHostname: hostname,
		isLocalhost,
		port,
	};
}

/**
 * Get tenant identifier from the URL subdomain
 */
export function getTenantIdentifier(): string | null {
	return detectSubdomain().subdomain;
}

/**
 * Validate tenant identifier format
 */
export function isValidTenantId(tenantId: string | null): boolean {
	if (!tenantId) return false;

	// Tenant ID should be alphanumeric with hyphens/underscores
	return /^[a-z0-9-_]+$/i.test(tenantId);
}
